import {
  type AsyncThunk,
  type AsyncThunkPayloadCreator,
  createAsyncThunk as createInternalAsyncThunk,
} from '@reduxjs/toolkit'
// Разобранная ошибка API (тип жил в features/notifications)
import { API_KNOWN_ERRORS } from '@shared-core/services/api/configs/api'

// Разобранная ошибка API (тип жил в features/notifications)
type ParsedError = {
  error: API_KNOWN_ERRORS
  value?: string
}

import { listenerMiddleware } from '@shared-core/store/listener-middleware'
import type { AppDispatch, AppState } from '@shared-core/store/types'

type ThunkConfigured = {
  state: AppState
  dispatch: AppDispatch
}

export const createAsyncThunk = createInternalAsyncThunk.withTypes<ThunkConfigured>()
export const startAppListening = listenerMiddleware.startListening.withTypes<AppState, AppDispatch>()

type AsyncCacheOptions = {
  ttl: number
}

type AsyncCacheEntry = {
  cachedAt: number
  value: unknown
}

const asyncCacheByType = new Map<string, AsyncCacheEntry>()

export const clearAsyncCache = (typePrefix?: string): void => {
  if (typePrefix) {
    asyncCacheByType.delete(typePrefix)
    return
  }

  asyncCacheByType.clear()
}

export const createAsyncCacheThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  options: AsyncCacheOptions,
  payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkConfigured>,
): AsyncThunk<Returned, ThunkArg, ThunkConfigured> => {
  return createAsyncThunk(typePrefix, async (arg, api) => {
    const cached = asyncCacheByType.get(typePrefix)

    if (cached && options.ttl > 0 && Date.now() - cached.cachedAt < options.ttl) {
      return cached.value as Returned
    }

    const result = await payloadCreator(arg, api)
    asyncCacheByType.set(typePrefix, { cachedAt: Date.now(), value: result })
    return result
  }) as AsyncThunk<Returned, ThunkArg, ThunkConfigured>
}

type TimeoutId = ReturnType<typeof setTimeout> | number

type DebounceSettings = {
  wait: number
  maxWait?: number
  leading?: boolean
}

// Debounced async thunk: coalesces rapid dispatches and supports leading/maxWait behavior.
export const createDebouncedAsyncThunk = <Returned, ThunkArg = void>(
  typePrefix: string,
  options: DebounceSettings,
  payloadCreator: AsyncThunkPayloadCreator<Returned, ThunkArg, ThunkConfigured>,
): AsyncThunk<Returned, ThunkArg, object> => {
  const { maxWait = 0, leading = false } = options ?? {}
  let timer: TimeoutId = 0
  let maxTimer: TimeoutId = 0
  let res: ((value: boolean) => void) | undefined
  const invoke = (): void => {
    clearTimeout(maxTimer)
    maxTimer = 0

    if (res) {
      res(true)
      res = undefined
    }
  }
  const cancel = (): void => {
    if (res) {
      res(false)
      res = undefined
    }
  }

  return createAsyncThunk(typePrefix, payloadCreator as never, {
    condition() {
      const immediate = leading && !timer
      clearTimeout(timer)
      timer = setTimeout(() => {
        invoke()
        timer = 0
      }, options.wait)

      if (immediate) return true

      cancel()

      if (maxWait && !maxTimer) maxTimer = setTimeout(invoke, maxWait)

      return new Promise<boolean>((resolve) => {
        res = resolve
      })
    },
  })
}

const knownErrors = Object.values(API_KNOWN_ERRORS)

const errorPatterns: {
  regex: RegExp
  code: API_KNOWN_ERRORS
}[] = [
  {
    regex: /minimum (\d+(?:[.,]\d+)?) ([A-Z]{2,10}) required/i,
    code: API_KNOWN_ERRORS.MUST_BE_GREATER_THAN_OR_EQUAL_SUM,
  },
  {
    regex: /maximum (\d+(?:[.,]\d+)?) ([A-Z]{2,10}) allowed/i,
    code: API_KNOWN_ERRORS.MUST_BE_LESS_THAN_OR_EQUAL_SUM,
  },
  {
    regex: /must be greater than or equal to (\d+(?:\.\d+)?)/i,
    code: API_KNOWN_ERRORS.MUST_BE_GREATER_THAN_OR_EQUAL_SUM,
  },
  {
    regex: /must be greater than (\d+(?:\.\d+)?)/i,
    code: API_KNOWN_ERRORS.MUST_BE_GREATER_THAN,
  },
  {
    regex: /must be less than or equal to (\d+(?:\.\d+)?)/i,
    code: API_KNOWN_ERRORS.MUST_BE_LESS_THAN_OR_EQUAL_SUM,
  },
  {
    regex: /must be less than (\d+(?:\.\d+)?)/i,
    code: API_KNOWN_ERRORS.MUST_BE_LESS_THAN,
  },
  {
    regex: /must be filled/i,
    code: API_KNOWN_ERRORS.MUST_BE_FILLED,
  },
  {
    regex: /not enough money for transfer/i,
    code: API_KNOWN_ERRORS.NOT_ENOUGH_MONEY_FOR_WITHDRAW,
  },
  {
    regex: /already exists/i,
    code: API_KNOWN_ERRORS.ALREADY_EXISTS,
  },
  {
    regex: /already have a pending topup with this amount/i,
    code: API_KNOWN_ERRORS.ALREADY_HAVE_PENDING_TOPUP_WITH_THIS_AMOUNT,
  },
]

// AML: бэк возвращает "RESULT_NOT_AVAILABLE. Status: pending" пока проверка еще идет.
// Мы намеренно игнорируем это, чтобы UI оставался в состоянии ожидания.
// Остальные статусы (например, "RESULT_NOT_AVAILABLE. Status: fail") заведены в API_KNOWN_ERRORS
// и должны показываться как реальные ошибки.
const amlResultPendingRegex = /RESULT_NOT_AVAILABLE\.\s*Status:\s*pending/i

// Управляющие коды бэка: не пользовательская ошибка, тост не показываем и НЕ подменяем на
// defaultError. ACCOUNT_DRAFT — 403 почти на всех ручках для draft-аккаунта (без почты); юзеру
// показывается штора «добавьте email». Без этого списка неизвестный код дропался, но непустой
// errorsList проваливал parseErrors в defaultResult и всплывал ложный «Ошибка запроса».
const IGNORED_CONTROL_CODES = new Set<string>(['ACCOUNT_DRAFT'])

const isIgnoredControlError = (error: unknown): boolean =>
  typeof error === 'string' && (IGNORED_CONTROL_CODES.has(error) || amlResultPendingRegex.test(error))

const prepareError = (error: unknown) => {
  if (!error) {
    return []
  }

  if (typeof error === 'string') {
    return [error]
  }

  if (Array.isArray(error)) {
    return error
  }

  return []
}

// Converts backend errors map into normalized app-level parsed errors.
export const parseErrors = (
  errors: Record<string, unknown> | undefined,
  meta?: Record<string, unknown> | undefined | null,
  defaultError?: ParsedError,
): ParsedError[] => {
  const defaultResult = defaultError ? [defaultError] : []

  if (!errors) {
    return defaultResult
  }

  const errorsList = Object.values(errors).flatMap(prepareError)

  if (errorsList.length > 0) {
    // Пришли только управляющие/pending-коды — подавляем целиком, без defaultResult-фоллбэка,
    // иначе draft-403 (ACCOUNT_DRAFT) подменялся бы на defaultError и показывал «Ошибка запроса».
    if (errorsList.every(isIgnoredControlError)) {
      return []
    }

    const result = errorsList.reduce<ParsedError[]>((acc, error: API_KNOWN_ERRORS) => {
      if (typeof error !== 'string' || isIgnoredControlError(error)) {
        return acc
      }

      if (knownErrors.includes(error)) {
        if (error === API_KNOWN_ERRORS.PAYMENT_AMOUNT_EXCEEDS_LIMIT_ERROR && meta) {
          acc.push({ error, value: meta.maxAmountAllowed as string | undefined })
        } else {
          acc.push({ error })
        }
      } else {
        for (const { regex, code } of errorPatterns) {
          const match = regex.exec(error)

          if (match) {
            // Группа 2 — валюта из текста бэка («maximum 400 TON allowed»).
            const amountPart = match[1]
            const coinPart = match[2]
            const value = amountPart && coinPart ? `${amountPart} ${coinPart}` : (amountPart ?? undefined)

            acc.push({ error: code, value })
          }
        }
      }

      return acc
    }, [])

    if (result.length > 0) {
      return result.filter((item, index) => result.findIndex((other) => other.error === item.error) === index)
    }
  }

  return defaultResult
}
